import type { Table as TanstackTable } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

type DataTableProps<TData> = {
  table: TanstackTable<TData>;
  emptyMessage?: string;
  className?: string;
  getRowClassName?: (row: TData) => string | undefined;
};

function stickyColumnClass(sticky: "left" | "right" | undefined, kind: "head" | "cell") {
  if (!sticky) return undefined;
  return cn(
    "sticky z-20 bg-card",
    sticky === "right" && "right-0 border-l border-border shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]",
    sticky === "left" && "left-0 border-r border-border shadow-[6px_0_8px_-6px_rgba(0,0,0,0.12)]",
    kind === "head" && "z-30",
    kind === "cell" && "group-hover/row:bg-muted/50",
  );
}

export function DataTable<TData>({
  table,
  emptyMessage = "暂无数据",
  className,
  getRowClassName,
}: DataTableProps<TData>) {
  const rows = table.getRowModel().rows;

  return (
    <div className={cn("overflow-hidden rounded-lg border border-border bg-card", className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                const sticky = header.column.columnDef.meta?.sticky;
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      header.column.columnDef.meta?.headerClassName,
                      stickyColumnClass(sticky, "head"),
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length > 0 ? (
            rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn("group/row", getRowClassName?.(row.original))}
              >
                {row.getVisibleCells().map((cell) => {
                  const sticky = cell.column.columnDef.meta?.sticky;
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        cell.column.columnDef.meta?.cellClassName,
                        stickyColumnClass(sticky, "cell"),
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
